import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../Context/UserContext";
import useAPI from "../../Hooks/useAPI";
import Header2 from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import {
  faArrowLeft,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../Components/Page/Page.css";
import "./UserManagement.css";
import { userInfo } from "os";

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  createdAt: string;
  mndaStatus: string;
}

interface UserActivities {
  usertrackingid: string;
  username: string;
  email: string;
  activity: string;
  transactiondate: string;
  additionalinfo: string;
  addedby: string;
}

interface ActivityType {
  activitytype: string;
}

interface UserTrackingPages {
  pagename: string;
}

export default function UserActivity() {
  const [users, setUsers] = useState<User[]>([]);
  const [useractivities, setUserActivities] = useState<UserActivities[]>([]);
  const [activitytypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [usertrackingpages, setUserTrackingPages] = useState<
    UserTrackingPages[]
  >([]);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { user, hasRole } = useUser();
  const navigate = useNavigate();
  const { getUsers, getActivityTypes, getUserActivity, getUserTrackingPages } =
    useAPI();
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [selectedActivtyType, setSelectedActivityType] = useState("all");
  const [selectedUserTrackingPage, setselectedUserTrackingPage] =
    useState("all");

  const [sortConfig, setSortConfig] = useState<{
    key: keyof UserActivities | null;
    direction: "ascending" | "descending" | null;
  }>({
    key: null,
    direction: null,
  });

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await getUsers("");
      if (response?.data) {
        setUsers(response.data);
      } else {
        throw new Error("Failed to fetch users");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred during fetchUsers"
      );
    } finally {
      setIsLoading(false);
    }
  }, [getUsers]);

  const fetchActivityTypes = useCallback(async () => {
    try {
      const response = await getActivityTypes();
      if (response?.data) {
        setActivityTypes(response.data);
      } else {
        throw new Error("Failed to fetch Activity Types");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred during fetchActivityTypes"
      );
    }
  }, []);

  const fetchUserTrackingPages = useCallback(async () => {
    try {
      const response = await getUserTrackingPages();
      if (response?.data) {
        setUserTrackingPages(response.data);
      } else {
        throw new Error("Failed to fetch User Tracking Pages");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred during fetchUserTrackingPages"
      );
    }
  }, []);

  const fetchUserActivity = useCallback(
    async (selUserid: string, selActivityType: string, selPageName: string) => {
      try {
        setIsLoading(true);
        setError("");
        //const selStatus = selectedContractStatus || "all";
        const response = await getUserActivity(
          selUserid,
          selActivityType,
          selPageName
        );
        if (response?.data) {
          setUserActivities(response.data);
        } else {
          throw new Error("Failed to fetch user Activity");
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "An error occurred during fetchUserActivity"
        );
      } finally {
        setIsLoading(false);
      }
    },
    [getUserActivity]
  );
  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      navigate("/login");
      return;
    }

    // Check if user has administrator role
    if (!hasRole("administrator")) {
      navigate("/");
      return;
    }

    fetchUsers();
    fetchActivityTypes();
    fetchUserTrackingPages();
  }, []);

  useEffect(() => {
    fetchUserActivity(
      selectedUserId,
      selectedActivtyType,
      selectedUserTrackingPage
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, selectedActivtyType, selectedUserTrackingPage]);

  const handleSort = (key: keyof UserActivities) => {
    let direction: "ascending" | "descending" | null = "ascending";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        direction = "descending";
      } else if (sortConfig.direction === "descending") {
        direction = null;
      }
    }

    setSortConfig({ key, direction });
  };

  const getSortedUsers = () => {
    if (!sortConfig.key || !sortConfig.direction) {
      return useractivities;
    }

    return [...useractivities].sort((a, b) => {
      if (a[sortConfig.key!].toLowerCase() < b[sortConfig.key!].toLowerCase()) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a[sortConfig.key!].toLowerCase() > b[sortConfig.key!].toLowerCase()) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (columnKey: keyof UserActivities) => {
    if (sortConfig.key !== columnKey) {
      return <FontAwesomeIcon icon={faSort} />;
    }
    return sortConfig.direction === "ascending" ? (
      <FontAwesomeIcon icon={faSortUp} />
    ) : (
      <FontAwesomeIcon icon={faSortDown} />
    );
  };
  const getStatusStyle = (status: string) => {
    if (status.toLowerCase().includes("rejected")) {
      return { color: "red" };
    } else if (status.toLowerCase().includes("approved")) {
      return { color: "green" };
    } else if (status.toLowerCase().includes("delete")) {
      return { color: "red" };
    } else {
      return {};
    }
  };
  return (
    <div className="generalPage">
      <Header2 />
      <div className="usermanagement-container">
        <div className="pageContainer">
          <div className="button-container">
            <button className="back-button" onClick={() => navigate("/")}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </button>
          </div>

          <div className="usermanagement-header">
            <div className="usermanagement-actions">
              <h1 className="usermanagement-title">User Activity</h1>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4">
            <label className="marginright">User</label>

            <select
              onChange={(e) => setSelectedUserId(e.target.value)}
              value={selectedUserId ?? ""}
            >
              <option value="all">-- All Users --</option>
              {users.map((u, uindex) => (
                <option key={uindex} value={u.id}>
                  {u.firstName} {u.lastName} - {u.email}
                </option>
              ))}
            </select>
            <label className="marginright marginleft">Activity Type</label>
            <select
              onChange={(e) => setSelectedActivityType(e.target.value)}
              value={selectedActivtyType}
            >
              <option value="all">-- All Activity Types --</option>
              {activitytypes.map((at, atindex) => (
                <option key={atindex} value={at.activitytype}>
                  {at.activitytype}
                </option>
              ))}
            </select>
            {selectedActivtyType === "Page Visited" && (
              <div>
                <label className="marginright marginleft">Page Name</label>
                <select
                  onChange={(e) => setselectedUserTrackingPage(e.target.value)}
                  value={selectedUserTrackingPage}
                >
                  <option value="all">-- All Pages --</option>
                  {usertrackingpages.map((utp, utpindex) => (
                    <option key={utpindex} value={utp.pagename}>
                      {utp.pagename}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {error && <div className="error-message">{error}</div>}

          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading...</div>
            </div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead className="table-header">
                  <tr>
                    <th
                      onClick={() => handleSort("username")}
                      className="sortable-header"
                    >
                      User Name {getSortIcon("username")}
                    </th>
                    <th
                      onClick={() => handleSort("email")}
                      className="sortable-header"
                    >
                      Email {getSortIcon("email")}
                    </th>
                    <th
                      onClick={() => handleSort("transactiondate")}
                      className="sortable-header"
                    >
                      Date {getSortIcon("transactiondate")}
                    </th>
                    <th
                      onClick={() => handleSort("activity")}
                      className="sortable-header"
                    >
                      Activity {getSortIcon("activity")}
                    </th>

                    <th
                      onClick={() => handleSort("additionalinfo")}
                      className="sortable-header"
                    >
                      Additional Info {getSortIcon("additionalinfo")}
                    </th>
                    <th
                      onClick={() => handleSort("addedby")}
                      className="sortable-header"
                    >
                      Added By {getSortIcon("addedby")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedUsers().map((useractivities) => (
                    <tr
                      key={useractivities.usertrackingid}
                      className="table-row"
                    >
                      <td>{useractivities.username}</td>
                      <td>{useractivities.email}</td>
                      <td>{useractivities.transactiondate}</td>
                      <td style={getStatusStyle(useractivities.activity)}>
                        {useractivities.activity}
                      </td>
                      <td>{useractivities.additionalinfo}</td>
                      <td>{useractivities.addedby}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
